import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, FileText, List, RotateCcw, Settings, LogOut, Menu, FileCheck, ClipboardList, CheckCircle, MessageSquare, Trash2 } from 'lucide-react';
import { getAuth, clearAuth } from '@/lib/auth';
import { useState } from 'react';

const navItems = [
  { label: 'Visao Geral', icon: LayoutDashboard, path: '/admin' },
  { label: 'Atendimento', icon: MessageSquare, path: '/admin/atendimento' },
  { label: 'Relatorio', icon: ClipboardList, path: '/admin/report' },
  { label: 'Clientes', icon: Users, path: '/admin/clients' },
  { label: 'Processos', icon: FileText, path: '/admin/cases' },
  { label: 'Documentos', icon: FileCheck, path: '/admin/documents' },
  { label: 'Listas', icon: List, path: '/admin/lists' },
  { label: 'Retornos', icon: RotateCcw, path: '/admin/returns' },
  { label: 'Baixas Concluidas', icon: CheckCircle, path: '/admin/concluidas' },
  { label: 'Lixeira', icon: Trash2, path: '/admin/trash' },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = getAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    clearAuth();
    navigate('/login');
  };

  const isActive = (path) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="flex min-h-screen" data-testid="admin-layout">
      {/* Mobile menu button */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-xl bg-[#0F1620] border border-white/10"
        onClick={() => setMobileOpen(!mobileOpen)}
        data-testid="admin-mobile-menu-button"
      >
        <Menu size={20} />
      </button>

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 w-[280px] border-r border-white/10 flex flex-col transition-transform lg:translate-x-0 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ background: 'rgba(15,20,24,0.95)', backdropFilter: 'blur(12px)' }}
        data-testid="admin-sidebar-nav"
      >
        {/* Logo */}
        <div className="p-6 border-b border-white/10">
          <img src="/logo-recupnet.png" alt="RecupNet" className="h-9" />
          <p className="text-xs text-white/50 mt-2">Painel Administrativo</p>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 px-3 space-y-1">
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => { navigate(item.path); setMobileOpen(false); }}
              data-testid={`admin-sidebar-item-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors relative ${
                isActive(item.path)
                  ? 'bg-white/[0.06] text-white shadow-[inset_0_0_0_1px_rgba(0,255,163,0.25)] sidebar-item-active'
                  : 'text-white/70 hover:text-white hover:bg-white/[0.04]'
              }`}
            >
              <item.icon size={18} className={isActive(item.path) ? 'text-[var(--rn-green)]' : ''} />
              {item.label}
            </button>
          ))}
        </nav>

        {/* User + Logout */}
        <div className="p-4 border-t border-white/10">
          <div className="text-sm text-white/60 mb-2 truncate">{auth?.user?.name}</div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 rounded-xl px-3 py-2 text-sm text-white/60 hover:text-white hover:bg-white/[0.04] transition-colors"
            data-testid="admin-logout-button"
          >
            <LogOut size={16} /> Sair
          </button>
          <p className="text-[9px] text-white/15 mt-3 text-center">v1.0-2026-02-26</p>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/60 z-30"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 lg:ml-0 min-h-screen">
        <div className="mx-auto w-full max-w-[1280px] px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
